import React from 'react';
import clsx from 'clsx';

import './DecisionDialog.css';
import Dialog, { DialogProps } from 'components/dialog/Dialog';

//
// --- Types ---

export interface DecisionDialogProps extends DialogProps {
  /** expects array of Button elements, or elements composed with BaseButton */
  actionButtons: Array<React.ReactElement>;
  className?: string;
  'date-testid'?: string;
  onCloseButtonClick?: () => void;
  onDialogClose?: () => void;
  showCloseButton?: boolean;
  title: string;
}

//
// --- DecisionDialog Component ---

/**
 * DecisionDialog - designed to handle proposing a choice to a user, or can be used
 * as acknowledgment of an action. This dialog display a title, some descriptive text,
 * an optional close button, and a set of action buttons.
 *
 * Typical use cases:
 *  - Multiple Buttons: The user needs to make a choice. This usually provide them
 *       with options to respond in a negating or affirmative manner.
 *  - Single Button: The user needs to acknowledge some information
 *
 * When providing choices, the "negating" option should be listed first and use
 * tertiary colors. The "affirmative" option should be second and use primary color.
 */

const DecisionDialog: React.FC<DecisionDialogProps> = props => {
  const { actionButtons, className, 'data-testid': dataTestId = 'decision-dialog', ...remainingDialogProps } = props;

  const affirmativeButton = React.cloneElement(actionButtons[actionButtons.length - 1], {
    color: 'primary',
    autoFocus: true
  });

  const negateButtons =
    actionButtons.length > 1
      ? actionButtons.slice(0, -1).map(button => React.cloneElement(button, { color: 'tertiary' }))
      : [];

  return (
    <Dialog
      actionContent={React.Children.toArray([...negateButtons, affirmativeButton])}
      className={clsx('decision-dialog', className)}
      data-testid={dataTestId}
      {...remainingDialogProps}
    />
  );
};

export default DecisionDialog;
